import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, map, merge, Subscription, throttleTime } from 'rxjs';
import { Nullable } from 'src/core/common/domain/types/types';
import { PresenceModalResult, PresenceModalService } from 'src/core/presence/infrastructure/presence-modal.service';
import { MasterDataService } from 'src/core/master-data/domain/master-data-service';
import { EventsService } from 'src/core/events/events.service';
import { UserLoggedOutEvent } from 'src/core/events/user-logged-out.event';
import { UserPresenceConfiguration } from 'src/core/master-data/domain/master-data-configuration';
import { InitializerService } from 'src/core/common/domain/initializer/initializer.service';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { ReportLostChanges } from 'src/core/presence/application/report-lost-changes/report-lost-changes';
import { ReportLostChangesRequest } from 'src/core/presence/application/report-lost-changes/report-lost-changes-request';
import { PresenceLogoutTrigger } from 'src/core/presence/domain/lost-changes-report';

@Injectable({
  providedIn: 'root',
})
export class UserPresenceService implements OnDestroy {
  private isStarted = false;
  private isEnabled = false;
  private isWarningOpen = false;
  private isLogoutInProgress = false;
  private isSynchronizing = false;
  // Counted rather than flagged: the download on resume can overlap the one the tasks page starts.
  private masterDataDownloadsInProgress = 0;
  private timerGeneration = 0;
  private activitySubscription: Nullable<Subscription> = null;
  private configSubscription: Nullable<Subscription> = null;
  private workSubscription: Nullable<Subscription> = null;
  private inactivityTimeoutId: number | null = null;
  private currentConfig: UserPresenceConfiguration;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ngZone: NgZone,
    private readonly masterDataService: MasterDataService,
    private readonly presenceModalService: PresenceModalService,
    private readonly eventsService: EventsService,
    private readonly queueService: QueueService,
    private readonly reportLostChanges: ReportLostChanges
  ) {}

  public ngOnDestroy(): void {
    this.stop();
  }

  public async start(): Promise<void> {
    await InitializerService.waitFor(this.masterDataService);

    if (this.isStarted) {
      return;
    }

    this.currentConfig = this.masterDataService.getUserPresenceConfiguration();
    this.isStarted = true;
    this.isLogoutInProgress = false;

    // Before the configuration, whose subscription arms the timer straight away: a pass that is already running
    // has to hold it from the start.
    this.subscribeToWorkInProgress();
    this.subscribeToConfigChanges();
    this.registerActivityListeners();
    this.updatePresenceConfiguration(this.currentConfig);
  }

  public stop(): void {
    // console.log('Stopping user presence service');
    this.timerGeneration += 1;
    this.isStarted = false;
    this.isEnabled = false;
    this.isWarningOpen = false;
    this.isLogoutInProgress = false;
    this.isSynchronizing = false;
    this.masterDataDownloadsInProgress = 0;

    void this.presenceModalService.closeWarningModal();
    this.clearInactivityTimer();
    this.unsubscribeActivityListeners();
    this.unsubscribeConfigChanges();
    this.unsubscribeWorkInProgress();
  }

  public notifyActivity(): void {
    if (!this.isStarted) {
      return;
    }

    if (!this.isEnabled) {
      return;
    }

    if (this.isWarningOpen) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    console.log(' ---> Activity detected, resetting inactivity timer');
    this.scheduleInactivityTimer();
  }

  public restart(): void {
    if (!this.isStarted) {
      return;
    }

    if (!this.isEnabled) {
      return;
    }

    if (this.isWarningOpen) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    this.scheduleInactivityTimer();
  }

  /**
   * A logout wipes the queue, so with presence control on nobody may log out, or switch user with a card, while
   * a pass is sending it.
   */
  public isLogoutBlocked(): boolean {
    return this.isEnabled && this.queueService.isProcessing;
  }

  private subscribeToConfigChanges(): void {
    this.unsubscribeConfigChanges();

    this.configSubscription = this.masterDataService.userPresenceConfiguration$.subscribe(
      (config: UserPresenceConfiguration) => {
        if (!this.isStarted) {
          return;
        }

        this.updatePresenceConfiguration(config);
      }
    );
  }

  private subscribeToWorkInProgress(): void {
    this.unsubscribeWorkInProgress();
    this.isSynchronizing = this.queueService.isProcessing;
    this.masterDataDownloadsInProgress = 0;

    this.workSubscription = new Subscription();
    this.workSubscription.add(
      this.eventsService.isSynchronizing$.subscribe((event) => {
        this.isSynchronizing = event.isSynchronizing;
        this.reactToWorkInProgress();
      })
    );
    this.workSubscription.add(
      this.eventsService.isDownloadingMasterData$.subscribe((event) => {
        this.masterDataDownloadsInProgress = event.isDownloading
          ? this.masterDataDownloadsInProgress + 1
          : Math.max(0, this.masterDataDownloadsInProgress - 1);
        this.reactToWorkInProgress();
      })
    );
  }

  private updatePresenceConfiguration(configuration: Nullable<UserPresenceConfiguration>): void {
    this.timerGeneration += 1;
    this.currentConfig = configuration;
    this.isEnabled = this.currentConfig?.enabled ?? false;
    this.isWarningOpen = false;

    void this.presenceModalService.closeWarningModal();
    this.clearInactivityTimer();

    if (!this.isEnabled) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    this.scheduleInactivityTimer();

    // console.log(`Starting user presence service:`);
    // console.log(` -> opens after ${configuration.delay_seconds}s of inactivity`);
    // console.log(` -> gives you ${configuration.response_timeout_seconds}s to respond`);
  }

  /**
   * Nobody is expected to touch the screen while data comes and goes, so that time does not count as inactivity:
   * the timer is held, and the user gets a full delay once the work ends.
   */
  private reactToWorkInProgress(): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    if (this.hasWorkInProgress()) {
      this.holdInactivityTimer();
      return;
    }

    this.scheduleInactivityTimer();
  }

  private holdInactivityTimer(): void {
    this.timerGeneration += 1;
    this.isWarningOpen = false;

    void this.presenceModalService.closeWarningModal();
    this.clearInactivityTimer();
  }

  private hasWorkInProgress(): boolean {
    return this.isSynchronizing || this.masterDataDownloadsInProgress > 0;
  }

  private registerActivityListeners(): void {
    this.unsubscribeActivityListeners();

    this.ngZone.runOutsideAngular((): void => {
      // using passive listeners, to avoid penalizing scroll
      const click$ = fromEvent(this.document, 'click').pipe(map((event) => ({ type: 'click', event })));

      const touchStart$ = fromEvent(this.document, 'touchstart', { passive: true }).pipe(
        map((event) => ({ type: 'touchstart', event }))
      );

      const keyDown$ = fromEvent(this.document, 'keydown').pipe(map((event) => ({ type: 'keydown', event })));

      // using throttle to capture only one event within the specified time interval (for events that are triggered very frequently)
      const touchMove$ = fromEvent(this.document, 'touchmove', { passive: true }).pipe(
        throttleTime(500, undefined, { leading: true, trailing: false }),
        map((event) => ({ type: 'touchmove', event }))
      );
      const wheel$ = fromEvent(this.document, 'wheel', { passive: true }).pipe(
        throttleTime(500, undefined, { leading: true, trailing: false }),
        map((event) => ({ type: 'wheel', event }))
      );

      const scroll$ = fromEvent(window, 'scroll', { passive: true }).pipe(
        throttleTime(500, undefined, { leading: true, trailing: false }),
        map((event) => ({ type: 'scroll', event }))
      );

      this.activitySubscription = merge(click$, touchStart$, touchMove$, keyDown$, wheel$, scroll$).subscribe(
        (): void => {
          // console.log('[UserPresence] Activity from:', type);
          this.notifyActivity();
        }
      );
    });
  }

  private unsubscribeActivityListeners(): void {
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = null;
  }

  private unsubscribeConfigChanges(): void {
    this.configSubscription?.unsubscribe();
    this.configSubscription = null;
  }

  private unsubscribeWorkInProgress(): void {
    this.workSubscription?.unsubscribe();
    this.workSubscription = null;
  }

  private scheduleInactivityTimer(): void {
    this.clearInactivityTimer();

    if (!this.isEnabled) {
      return;
    }

    if (this.hasWorkInProgress()) {
      return;
    }

    if (!this.currentConfig) {
      return;
    }

    if (this.currentConfig.delay_seconds === null) {
      return;
    }

    const timerGeneration = this.timerGeneration;
    this.inactivityTimeoutId = window.setTimeout((): void => {
      void this.handleInactivityTimeout(timerGeneration);
    }, this.currentConfig.delay_seconds * 1000);
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimeoutId !== null) {
      window.clearTimeout(this.inactivityTimeoutId);
      this.inactivityTimeoutId = null;
    }
  }

  private async handleInactivityTimeout(timerGeneration: number): Promise<void> {
    if (!this.isStarted) {
      return;
    }

    if (!this.isCurrentTimerGeneration(timerGeneration)) {
      return;
    }

    if (!this.isEnabled) {
      return;
    }

    if (this.isWarningOpen) {
      return;
    }

    if (this.isLogoutInProgress) {
      return;
    }

    if (this.hasWorkInProgress()) {
      return;
    }

    this.isWarningOpen = true;

    try {
      const result: PresenceModalResult = await this.presenceModalService.openWarningModal(
        this.currentConfig.response_timeout_seconds
      );

      if (!this.isStarted) {
        return;
      }

      if (!this.isCurrentTimerGeneration(timerGeneration)) {
        return;
      }

      if (!this.isEnabled) {
        return;
      }

      if (result === 'timeout' || result === 'logout') {
        this.executeLogout(this.logoutTriggerOf(result));
        return;
      }

      this.isWarningOpen = false;
      this.scheduleInactivityTimer();
    } finally {
      // Work that started meanwhile already moved on to a new generation, whose state is not this one's to reset.
      if (this.isCurrentTimerGeneration(timerGeneration)) {
        this.isWarningOpen = false;
      }
    }
  }

  /**
   * Synchronous from the decision to the event: a pass that started in between would be wiped by the logout
   * half-way through.
   */
  private executeLogout(trigger: PresenceLogoutTrigger): void {
    if (this.isLogoutInProgress) {
      return;
    }

    this.isLogoutInProgress = true;
    this.clearInactivityTimer();
    this.reportLostChangesIgnoringFailures(trigger);

    try {
      this.eventsService.publishLoggedOutEvent(new UserLoggedOutEvent());
    } catch (error) {
      console.error('Error during automatic logout by inactivity', error);
    }
  }

  private reportLostChangesIgnoringFailures(trigger: PresenceLogoutTrigger): void {
    try {
      this.reportLostChanges.execute(new ReportLostChangesRequest(trigger));
    } catch (error) {
      console.error('Could not report the changes lost on inactivity logout', error);
    }
  }

  private logoutTriggerOf(result: 'timeout' | 'logout'): PresenceLogoutTrigger {
    if (result === 'logout') {
      return 'warning-modal-button';
    }

    return 'timeout';
  }

  private isCurrentTimerGeneration(timerGeneration: number): boolean {
    return this.timerGeneration === timerGeneration;
  }
}
